import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import FormInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import FormSelect from 'components/ui/composed/declarations/formSelect/DeclarationSelect';
import useCodelists from 'hooks/useCodelists';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import DeclarationField from 'views/declarations/common/DeclarationField';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { goodsShipmentItemSchemaRequired } from 'views/declarations/ireland/import/h1/validation/ValidationSchema';
import type { ItemFieldsRegistry } from './itemFieldsRegistry';

// Loads the AIS codelists a field needs (packaging types) if they haven't been fetched yet -
// this modal can be the first thing on screen to need them, unlike the full Ireland H1 form.
const useAisCodelists = () => {
    const { aisCodelists, getIrelandCodelists } = useCodelists();

    useEffect(() => {
        if (!aisCodelists) getIrelandCodelists(DeclarationTypes.AIS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return aisCodelists;
};

const CommodityCodeField = () => {
    const { t } = useTranslation('form');

    return (
        <DeclarationField name="goodsInformation.combinedNomenclatureCode">
            {({ form, field }) => (
                <FormInput
                    required
                    maxLength={8}
                    label={t('commodityCode')}
                    {...getFormikProps(field.name, form)}
                    condensed
                />
            )}
        </DeclarationField>
    );
};

const TaricCodeField = () => {
    const { t } = useTranslation('form');

    return (
        <DeclarationField name="goodsInformation.taricCode">
            {({ form, field }) => (
                <FormInput
                    required
                    maxLength={2}
                    label={t('goodsItems.taricCode')}
                    {...getFormikProps(field.name, form)}
                    condensed
                />
            )}
        </DeclarationField>
    );
};

const DescriptionField = () => {
    const { t } = useTranslation('form');

    return (
        <DeclarationField name="goodsInformation.goodsDescription">
            {({ form, field }) => (
                <FormInput
                    required
                    maxLength={512}
                    label={t('goodsItems.goodsDescription')}
                    {...getFormikProps(field.name, form)}
                    condensed
                />
            )}
        </DeclarationField>
    );
};

const NetMassField = () => {
    const { t } = useTranslation('form');

    return (
        <DeclarationField name="goodsInformation.netMass">
            {({ form, field }) => (
                <DeclarationNumberInput
                    required
                    label={t('goodsItems.netMass')}
                    {...getFormikProps(field.name, form)}
                    condensed
                />
            )}
        </DeclarationField>
    );
};

const PackagingTypeField = () => {
    const { t } = useTranslation('form');
    const aisCodelists = useAisCodelists();

    return (
        <DeclarationField name="goodsInformation.packaging.0.typePackage">
            {({ form, field }) => (
                <FormSelect
                    required
                    label={t('packages.packageType')}
                    {...getFormikProps(field.name, form)}
                    selectOptions={aisCodelists?.kindOfPackages}
                    condensed
                    codeValidation={[]}
                />
            )}
        </DeclarationField>
    );
};

const PackagingCountField = () => {
    const { t } = useTranslation('form');

    return (
        <DeclarationField name="goodsInformation.packaging.0.packageNumber">
            {({ form, field }) => (
                <DeclarationNumberInput
                    required
                    integerNumbers
                    label={t('packages.numberOfPackages')}
                    {...getFormikProps(field.name, form)}
                    condensed
                />
            )}
        </DeclarationField>
    );
};

// Keyed to match the reduced, mirroring-specific table column set (see irelandH1ItemColumns.tsx) -
// commodity code, TARIC code and description are separate columns, as is packaging type/count.
export const irelandH1ColumnErrorPaths: Record<string, string[]> = {
    commodity_code: ['goodsInformation.combinedNomenclatureCode'],
    taric_code: ['goodsInformation.taricCode'],
    description_of_goods: ['goodsInformation.goodsDescription'],
    net_mass: ['goodsInformation.netMass'],
    packages_type: ['goodsInformation.packaging.0.typePackage'],
    packages_count: ['goodsInformation.packaging.0.packageNumber'],
};

const irelandH1ColumnLabels: Record<string, string> = {
    commodity_code: 'Commodity Code',
    taric_code: 'Taric Code',
    description_of_goods: 'Description',
    net_mass: 'Net Mass',
    packages_type: 'Packages Type',
    packages_count: 'Packages Count',
};

// The progress sidebar checklist - derived from the same column keys as the table/validation
// schema above, so a field can only be checked here if it's actually displayed and fixable.
export const irelandH1FieldChecklist: { path: string; label: string }[] = Object.entries(irelandH1ColumnErrorPaths).map(
    ([key, paths]) => ({ path: paths[0], label: irelandH1ColumnLabels[key] ?? key })
);

// Wraps a single leaf schema (reached from goodsShipmentItemSchemaRequired) back into a nested
// schema at its original path, e.g. 'goodsInformation.netMass' -> { goodsInformation: { netMass } }.
// A purely-numeric segment (e.g. the '0' in 'packaging.0.typePackage') means the parent is an
// array - it must be wrapped with Yup.array().of(...), not a plain object keyed by "0". Yup's
// object schemas type-check via Object.prototype.toString, which real arrays fail, so a populated
// array would otherwise be rejected outright instead of being validated field-by-field.
const wrapAtPath = (segments: string[], leaf: Yup.AnySchema): Yup.AnySchema => {
    const [head, ...rest] = segments;
    const inner = rest.length ? wrapAtPath(rest, leaf) : leaf;
    if (/^\d+$/.test(head)) return Yup.array().of(inner as Yup.AnyObjectSchema);
    return Yup.object().shape({ [head]: inner });
};

// Builds a combined Yup schema covering exactly the given paths, reusing the real field-level
// validators from goodsShipmentItemSchemaRequired instead of duplicating validation rules.
const buildFieldSchema = (paths: string[]): Yup.AnyObjectSchema =>
    paths.reduce<Yup.AnyObjectSchema>((acc, path) => {
        const leaf = Yup.reach(goodsShipmentItemSchemaRequired, path) as Yup.AnySchema;
        // Every path here starts with a non-numeric top-level key (e.g. 'goodsInformation'),
        // so wrapAtPath always returns an object schema at this level.
        return acc.concat(wrapAtPath(path.split('.'), leaf) as Yup.AnyObjectSchema);
    }, Yup.object());

export const irelandH1ItemFieldsRegistry: ItemFieldsRegistry = {
    commodity_code: {
        Component: CommodityCodeField,
        validationSchema: buildFieldSchema(irelandH1ColumnErrorPaths.commodity_code),
    },
    taric_code: {
        Component: TaricCodeField,
        validationSchema: buildFieldSchema(irelandH1ColumnErrorPaths.taric_code),
    },
    description_of_goods: {
        Component: DescriptionField,
        validationSchema: buildFieldSchema(irelandH1ColumnErrorPaths.description_of_goods),
    },
    net_mass: {
        Component: NetMassField,
        validationSchema: buildFieldSchema(irelandH1ColumnErrorPaths.net_mass),
    },
    packages_type: {
        Component: PackagingTypeField,
        validationSchema: buildFieldSchema(irelandH1ColumnErrorPaths.packages_type),
    },
    packages_count: {
        Component: PackagingCountField,
        validationSchema: buildFieldSchema(irelandH1ColumnErrorPaths.packages_count),
    },
};

// The overall pass/fail check for an item is deliberately limited to only the fields shown in
// the table/quick-fix modal - fields required by goodsShipmentItemSchemaRequired but not
// displayed (e.g. item price) are not surfaced anywhere the user could fix them, so they must
// not be able to fail an item either.
export const irelandH1DisplayedFieldsValidationSchema: Yup.AnyObjectSchema = buildFieldSchema(
    Object.values(irelandH1ColumnErrorPaths).flat()
);
